/**
 * Turno Form Dialog - Form for generating new turns
 */

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

// Form schema
const turnoFormSchema = z.object({
  prioridad: z.enum(["BAJA", "MEDIA", "ALTA"]),
  tipo_servicio: z.string().optional(),
  observaciones: z.string().optional(),
});

type TurnoFormValues = z.infer<typeof turnoFormSchema>;

interface TurnoFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: TurnoFormValues) => void;
  isLoading?: boolean;
  sucursalNombre?: string;
}

export function TurnoFormDialog({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
  sucursalNombre,
}: TurnoFormDialogProps) {
  const { t } = useTranslation();

  const form = useForm<TurnoFormValues>({
    resolver: zodResolver(turnoFormSchema),
    defaultValues: {
      prioridad: "BAJA",
      tipo_servicio: "",
      observaciones: "",
    },
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      form.reset({
        prioridad: "BAJA",
        tipo_servicio: "",
        observaciones: "",
      });
    }
  }, [open, form]);

  const handleSubmit = (data: TurnoFormValues) => {
    onSubmit(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("turnos.newTurno")}</DialogTitle>
          <DialogDescription>
            {t("turnos.newTurnoDesc", "Generar un nuevo turno de atención")}
            {sucursalNombre && (
              <span className="block mt-1 font-medium text-foreground">{sucursalNombre}</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="prioridad"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("turnos.fields.priority", "Prioridad")}</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t("turnos.selectPriority", "Seleccione prioridad")}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="BAJA">
                        <span className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-green-500" />
                          {t("turnos.priority.low", "Baja")}
                        </span>
                      </SelectItem>
                      <SelectItem value="MEDIA">
                        <span className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-yellow-500" />
                          {t("turnos.priority.medium", "Media")}
                        </span>
                      </SelectItem>
                      <SelectItem value="ALTA">
                        <span className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-red-500" />
                          {t("turnos.priority.high", "Alta")}
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {t("turnos.priorityDesc", "Los turnos de alta prioridad se atienden primero")}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tipo_servicio"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("turnos.fields.serviceType", "Tipo de Servicio")}</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t("turnos.selectService", "Seleccione servicio")}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="examenes">
                        {t("turnos.serviceTypes.exams", "Exámenes de laboratorio")}
                      </SelectItem>
                      <SelectItem value="resultados">
                        {t("turnos.serviceTypes.results", "Entrega de resultados")}
                      </SelectItem>
                      <SelectItem value="cotizacion">
                        {t("turnos.serviceTypes.quote", "Cotización")}
                      </SelectItem>
                      <SelectItem value="otro">{t("turnos.serviceTypes.other", "Otro")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="observaciones"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("turnos.fields.observations", "Observaciones")}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t(
                        "turnos.observationsPlaceholder",
                        "Notas adicionales (opcional)"
                      )}
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("turnos.generateTurno", "Generar Turno")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
